import { Calendar } from "@untitledui/icons";
import { useEffect, useState } from "react";
import {
  customRangeLabel,
  DEFAULT_RANGE_ID,
  type DistributionRange,
  daysInRange,
  PRESET_RANGES,
  type Selection,
} from "../lib/ranges";

type RangeToggleProps = {
  range: DistributionRange;
  maxDate: string;
  onChange: (selection: Selection) => void;
  minDays?: number;
};

const addDays = (date: string, days: number) => {
  const shifted = new Date(`${date}T00:00:00Z`);
  shifted.setUTCDate(shifted.getUTCDate() + days);
  return shifted.toISOString().slice(0, 10);
};

const pillClass = (selected: boolean) =>
  [
    "cursor-pointer rounded-full px-3.5 py-1.5 text-[13px] font-medium transition-colors duration-150",
    selected ? "bg-[#21133a] text-white" : "text-[#21133a]/70 hover:bg-gray-50",
  ].join(" ");

export function RangeToggle({ range, maxDate, onChange, minDays = 1 }: RangeToggleProps) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({ from: "", to: "" });

  useEffect(() => {
    if (!open) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [open]);

  const isCustom = range.id === "custom";
  const withinBounds = (date: string) => date !== "" && date <= maxDate;
  const canApply =
    withinBounds(draft.from) &&
    withinBounds(draft.to) &&
    draft.from <= draft.to &&
    daysInRange(draft.from, draft.to) >= minDays;
  const latestFrom = addDays(draft.to && draft.to < maxDate ? draft.to : maxDate, 1 - minDays);

  return (
    <div
      role="group"
      aria-label="Time range"
      className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white p-1 shadow-xs"
    >
      {PRESET_RANGES.map((preset) => (
        <button
          key={preset.id}
          type="button"
          aria-pressed={range.id === preset.id}
          onClick={() => onChange({ id: preset.id })}
          className={pillClass(range.id === preset.id)}
        >
          {preset.label}
        </button>
      ))}

      <div className="relative">
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen((current) => !current)}
          className={[
            "inline-flex cursor-pointer items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[13px] font-medium transition-colors duration-150",
            isCustom ? "bg-[#21133a] text-white" : "text-[#21133a]/70 hover:bg-gray-50",
          ].join(" ")}
        >
          <Calendar width={14} height={14} aria-hidden="true" className="shrink-0" />
          {isCustom ? customRangeLabel(range.from, range.to) : "Custom"}
        </button>

        {open && (
          <>
            <div aria-hidden="true" onClick={() => setOpen(false)} className="fixed inset-0 z-20" />
            <div className="absolute right-0 z-40 mt-2 flex w-[280px] flex-col gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-lg">
              <p className="text-[13px] font-semibold text-[#21133a]">Custom range</p>
              <label className="flex flex-col gap-1 text-[12px] text-[#21133a]/70">
                From
                <input
                  type="date"
                  data-testid="custom-from"
                  value={draft.from}
                  max={latestFrom}
                  onChange={(event) => setDraft((current) => ({ ...current, from: event.target.value }))}
                  className="rounded-lg border border-gray-200 px-2 py-1.5 text-[13px] text-[#21133a]"
                />
              </label>
              <label className="flex flex-col gap-1 text-[12px] text-[#21133a]/70">
                To
                <input
                  type="date"
                  data-testid="custom-to"
                  value={draft.to}
                  min={draft.from ? addDays(draft.from, minDays - 1) : undefined}
                  max={maxDate}
                  onChange={(event) => setDraft((current) => ({ ...current, to: event.target.value }))}
                  className="rounded-lg border border-gray-200 px-2 py-1.5 text-[13px] text-[#21133a]"
                />
              </label>
              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => {
                    setDraft({ from: "", to: "" });
                    onChange({ id: DEFAULT_RANGE_ID });
                  }}
                  className="cursor-pointer text-[13px] font-medium text-[#21133a]/70 hover:underline"
                >
                  Clear
                </button>
                <button
                  type="button"
                  disabled={!canApply}
                  onClick={() => {
                    onChange({ id: "custom", from: draft.from, to: draft.to });
                    setOpen(false);
                  }}
                  className="cursor-pointer rounded-full bg-[#21133a] px-3.5 py-1.5 text-[13px] font-medium text-white disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Apply
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
